import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const PERMISSION_LABELS = {
  gestionar_empleados: 'Gestionar empleados',
  gestionar_horarios: 'Gestionar horarios',
  gestionar_sitios: 'Gestionar sitios',
  ver_monitor: 'Ver monitor',
  ver_calendario: 'Ver calendario',
  descargar_reportes: 'Descargar reportes',
  gestionar_permisos: 'Gestionar permisos',
  gestionar_aclaraciones: 'Gestionar aclaraciones',
  gestionar_legislacion: 'Gestionar legislación',
  gestionar_empresa: 'Gestionar empresa',
  gestionar_biometria: 'Gestionar biometría',
  ver_expedientes: 'Ver expedientes de Capital Humano',
  editar_expedientes: 'Editar expedientes de Capital Humano'
};

const SCOPE_LABELS = {
  EMPLOYEE: 'Empleados',
  DEPARTMENT: 'Departamentos',
  SITE: 'Sitios',
  OPERATION: 'Operaciones'
};

const emptyScope = () => ({
  EMPLOYEE: new Set(),
  DEPARTMENT: new Set(),
  SITE: new Set(),
  OPERATION: new Set()
});

export default function AdminManagement({ token, isDarkMode, companyId, effectiveRole }) {
  const [data, setData] = useState(null);
  const [scopeData, setScopeData] = useState(null);
  const [scopeSelected, setScopeSelected] = useState(emptyScope());
  const [selectedId, setSelectedId] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [form, setForm] = useState({ name: '', email: '', accountStatus: 'ACTIVA', adminLevel: 2, managerUserId: '' });
  const [permissions, setPermissions] = useState({});
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', adminLevel: 1, managerUserId: '' });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [promotionLevel, setPromotionLevel] = useState(1);
  const [promotionManagerId, setPromotionManagerId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingScope, setSavingScope] = useState(false);
  const [message, setMessage] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const bg = isDarkMode ? '#0f172a' : '#f8fafc';
  const card = isDarkMode ? '#1e293b' : '#fff';
  const text = isDarkMode ? '#f8fafc' : '#1f2937';
  const muted = isDarkMode ? '#94a3b8' : '#718096';
  const border = isDarkMode ? '#334155' : '#e2e8f0';
  const isN2 = Number(data?.actor?.adminLevel) === 2 && data?.actor?.role === 'Administrador';
  const canCreate = ['SuperAdministrador', 'SuperAdministradorEmpresarial'].includes(effectiveRole) || isN2;
  const canPromoteToN2 = ['SuperAdministrador', 'SuperAdministradorEmpresarial'].includes(effectiveRole);

  async function loadScope(adminId) {
    if (!adminId) {
      setScopeData(null);
      setScopeSelected(emptyScope());
      return;
    }

    try {
      const response = await axios.get('/api/admin-scope/context', {
        headers,
        params: { companyId, adminUserId: adminId }
      });
      setScopeData(response.data);
      const a = response.data.assignments || {};
      setScopeSelected({
        EMPLOYEE: new Set((a.EMPLOYEE || []).map(x => String(x.resourceId))),
        DEPARTMENT: new Set((a.DEPARTMENT || []).map(x => String(x.resourceId))),
        SITE: new Set((a.SITE || []).map(x => String(x.resourceId))),
        OPERATION: new Set((a.OPERATION || []).map(x => String(x.operationKey)))
      });
    } catch (error) {
      setScopeData(null);
      setScopeSelected(emptyScope());
      setMessage(error.response?.data?.message || 'No fue posible cargar el alcance del administrador.');
    }
  }

  async function load(id = '') {
    setLoading(true);
    setMessage('');
    try {
      const response = await axios.get('/api/admin-management/context', {
        headers,
        params: { companyId, ...(id ? { adminUserId: id } : {}) }
      });
      setData(response.data);
      const selected = response.data.selectedAdmin;
      if (selected) {
        setSelectedId(String(selected.id));
        setForm({
          name: selected.name || '',
          email: selected.email || '',
          accountStatus: selected.accountStatus || 'ACTIVA',
          adminLevel: selected.adminLevel || 2,
          managerUserId: selected.managerUserId || ''
        });
        setPermissions({ ...(selected.effectivePermissions || {}) });
        await loadScope(selected.id);
      } else {
        setScopeData(null);
        setScopeSelected(emptyScope());
      }
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible cargar la administración avanzada.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  const loadEmployees = async () => {
    try {
      const response = await axios.get('/api/users/all-with-attendance', { headers });
      const rows = Array.isArray(response.data) ? response.data.filter(user => user.role === 'Empleado') : [];
      setEmployees(rows);
      setSelectedEmployeeId(prev => prev && rows.some(user => String(user.id) === String(prev)) ? prev : '');
    } catch (error) {
      setEmployees([]);
    }
  };

  useEffect(() => {
    load('');
    loadEmployees();
  }, [companyId, token]);

  useEffect(() => {
    if (!canPromoteToN2) {
      setPromotionLevel(1);
      if (isN2 && data?.actor?.id) setPromotionManagerId(String(data.actor.id));
    }
  }, [canPromoteToN2, isN2, data?.actor?.id]);

  const choose = id => {
    setSelectedId(String(id || ''));
    load(id);
  };

  const togglePermission = key => setPermissions(prev => ({ ...prev, [key]: !prev[key] }));

  const toggleScope = (type, value) => {
    setScopeSelected(prev => {
      const next = new Set(prev[type]);
      const normalized = String(value);
      if (next.has(normalized)) next.delete(normalized);
      else next.add(normalized);
      return { ...prev, [type]: next };
    });
  };

  const scopeAssignments = () => [
    ...[...scopeSelected.EMPLOYEE].map(resourceId => ({ scopeType: 'EMPLOYEE', resourceId: Number(resourceId) })),
    ...[...scopeSelected.DEPARTMENT].map(resourceId => ({ scopeType: 'DEPARTMENT', resourceId: Number(resourceId) })),
    ...[...scopeSelected.SITE].map(resourceId => ({ scopeType: 'SITE', resourceId: Number(resourceId) })),
    ...[...scopeSelected.OPERATION].map(operationKey => ({ scopeType: 'OPERATION', operationKey }))
  ];

  async function saveAccount() {
    if (!selectedId || !data?.canEditSelected) return;
    setSaving(true); setMessage('');
    try {
      await axios.put(`/api/admin-management/${selectedId}/account`, form, { headers });
      setMessage('✓ Cuenta administrativa actualizada.');
      await load(selectedId);
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible actualizar la cuenta.');
    } finally { setSaving(false); }
  }

  async function togglePeerManagementDelegation() {
    if (!selectedId || !data?.canManagePeerManagementDelegation) return;
    setSaving(true);
    setMessage('');
    try {
      const enabled = Boolean(selected?.adminPeerManagementDelegated);
      await axios({
        method: enabled ? 'delete' : 'post',
        url: `/api/admin-management/${selectedId}/delegation`,
        headers
      });
      setMessage(enabled ? '✓ Delegación empresarial revocada.' : '✓ Delegación empresarial concedida.');
      await load(selectedId);
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible actualizar la delegación empresarial.');
    } finally { setSaving(false); }
  }

  async function savePermissions() {
    if (!selectedId || !data?.canEditSelected) return;
    setSaving(true); setMessage('');
    try {
      await axios.put(`/api/admin-management/${selectedId}/permissions`, { permissions }, { headers });
      setMessage('✓ Permisos administrativos actualizados.');
      await load(selectedId);
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible actualizar los permisos.');
    } finally { setSaving(false); }
  }

  async function saveScope() {
    if (!selectedId || !scopeData?.canEditSelected) return;
    setSavingScope(true); setMessage('');
    try {
      await axios.put(`/api/admin-scope/${selectedId}`, {
        assignments: scopeAssignments(),
        companyId
      }, { headers });
      setMessage('✓ Alcance administrativo actualizado.');
      await loadScope(selectedId);
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible actualizar el alcance administrativo.');
    } finally { setSavingScope(false); }
  }

  async function createAdmin() {
    setSaving(true); setMessage('');
    try {
      await axios.post('/api/admin-management', { ...newAdmin, companyId }, { headers });
      setMessage('✓ Administrador creado correctamente.');
      setNewAdmin({ name: '', email: '', password: '', adminLevel: 1, managerUserId: isN2 ? String(data?.actor?.id || '') : '' });
      await load('');
      await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible crear el administrador.');
    } finally { setSaving(false); }
  }

  async function promoteEmployee() {
    if (!selectedEmployeeId) {
      setMessage('Selecciona un empleado para promover.');
      return;
    }
    if (Number(promotionLevel) === 1 && !promotionManagerId) {
      setMessage('Selecciona el Administrador N2 superior para el nuevo Administrador N1.');
      return;
    }

    setSaving(true); setMessage('');
    try {
      await axios.put(`/api/users/${selectedEmployeeId}`, {
        role: 'Administrador',
        adminLevel: Number(promotionLevel),
        managerUserId: Number(promotionLevel) === 1 ? Number(promotionManagerId) : null
      }, { headers });
      setMessage('✓ Empleado promovido a Administrador correctamente.');
      setSelectedEmployeeId('');
      setPromotionLevel(isN2 ? 1 : promotionLevel);
      setPromotionManagerId(isN2 ? String(data?.actor?.id || '') : '');
      await load('');
      await loadEmployees();
    } catch (error) {
      setMessage(error.response?.data?.message || 'No fue posible promover al empleado.');
    } finally { setSaving(false); }
  }

  if (loading && !data) return <section style={{ padding: 24, color: text }}>Cargando administración avanzada…</section>;
  if (!data) return <section style={{ padding: 24, color: text }}>{message || 'Sin datos.'}</section>;

  const managers = (data.admins || []).filter(a => Number(a.adminLevel) === 2);
  const selected = data.selectedAdmin;
  const visibleAdmins = (data.admins || []).filter(admin => {
    const q = adminSearch.trim().toLowerCase();
    if (!q) return true;
    return [admin.name, admin.email, admin.id, admin.adminLevel, admin.accountStatus]
      .map(v => String(v ?? '').toLowerCase())
      .join(' ')
      .includes(q);
  });

  const scopeList = type => {
    if (!scopeData) return [];
    if (type === 'EMPLOYEE') return (scopeData.employees || []).map(x => ({ id: x.id, label: `${x.name} · ${x.email}` }));
    if (type === 'DEPARTMENT') return (scopeData.departments || []).map(x => ({ id: x.id, label: `${x.name}${x.code ? ` · ${x.code}` : ''}` }));
    if (type === 'SITE') return (scopeData.sites || []).map(x => ({ id: x.id, label: x.name }));
    return (scopeData.operations || []).map(x => ({ id: x, label: x }));
  };

  return (
    <section style={{ padding: 20, background: bg, borderRadius: 12, color: text }}>
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
        <h2 style={{ marginTop: 0 }}>🛡️ Administración avanzada</h2>
        <p style={{ color: muted, marginTop: 0 }}>
          La plantilla administrativa, expedientes, cuentas, jerarquía, permisos, delegación y alcance se gestionan desde la ficha del administrador seleccionado.
        </p>
        {message && <div style={{ marginBottom: 14, padding: 10, borderRadius: 8, border: `1px solid ${border}` }}>{message}</div>}

        <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0 }}>👥 Plantilla de administradores</h3>
              <div style={{ color: muted, fontSize: 12, marginTop: 4 }}>Selecciona a cualquier administrador para abrir su ficha y modificar lo que tu jerarquía permite.</div>
            </div>
            <input value={adminSearch} onChange={e => setAdminSearch(e.target.value)} placeholder="Buscar administrador…" style={{ minWidth: 240, padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 10, marginTop: 14 }}>
            {visibleAdmins.map(admin => {
              const active = String(admin.id) === String(selectedId);
              const manager = (data.admins || []).find(a => Number(a.id) === Number(admin.managerUserId));
              return (
                <button
                  key={admin.id}
                  type="button"
                  onClick={() => choose(admin.id)}
                  style={{ textAlign: 'left', padding: 12, borderRadius: 9, border: `1px solid ${active ? '#3182ce' : border}`, background: active ? (isDarkMode ? '#172554' : '#ebf8ff') : card, color: text, cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <strong>{admin.name}</strong>
                    <span style={{ fontSize: 10, fontWeight: 800 }}>N{admin.adminLevel || 2}</span>
                  </div>
                  <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>{admin.email}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                    <span style={{ fontSize: 10, padding: '3px 6px', borderRadius: 5, background: String(admin.accountStatus).toUpperCase() === 'ACTIVA' ? '#e6fffa' : '#fff5f5', color: String(admin.accountStatus).toUpperCase() === 'ACTIVA' ? '#276749' : '#c53030' }}>{admin.accountStatus || 'ACTIVA'}</span>
                    {manager && <span style={{ fontSize: 10, padding: '3px 6px', borderRadius: 5, background: isDarkMode ? '#334155' : '#edf2f7', color: text }}>Superior: {manager.name}</span>}
                  </div>
                </button>
              );
            })}
            {!visibleAdmins.length && <div style={{ color: muted, padding: 12 }}>No hay administradores que coincidan.</div>}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px,1fr) minmax(260px,1fr)', gap: 16, marginTop: 16 }}>
          <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>⬆️ Promover empleado a administrador</h3>
            <p style={{ color: muted, fontSize: 12, marginTop: 0 }}>Convierte la cuenta del empleado seleccionado utilizando la jerarquía administrativa existente.</p>
            <div style={{ display: 'grid', gap: 8 }}>
              <select value={selectedEmployeeId} onChange={e => setSelectedEmployeeId(e.target.value)} disabled={saving} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                <option value="">Selecciona un empleado…</option>
                {employees.map(employee => <option key={employee.id} value={employee.id}>{employee.name} · {employee.email}</option>)}
              </select>
              <select value={promotionLevel} onChange={e => { const level = Number(e.target.value); setPromotionLevel(level); setPromotionManagerId(level === 2 ? '' : (isN2 ? String(data?.actor?.id || '') : '')); }} disabled={saving} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                <option value={1}>Administrador N1</option>
                {canPromoteToN2 && <option value={2}>Administrador N2</option>}
              </select>
              {Number(promotionLevel) === 1 && <select value={promotionManagerId} onChange={e => setPromotionManagerId(e.target.value)} disabled={saving || isN2} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                <option value="">Selecciona N2 superior…</option>
                {managers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>}
              <button type="button" onClick={promoteEmployee} disabled={saving || !employees.length} style={{ padding: 10, border: 0, borderRadius: 8, cursor: 'pointer' }}>{saving ? 'Guardando…' : 'Promover empleado'}</button>
            </div>
          </div>

          {canCreate && <div style={{ border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>Crear administrador</h3>
            <div style={{ display: 'grid', gap: 8 }}>
              <input placeholder="Nombre" value={newAdmin.name} onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}` }} />
              <input placeholder="Correo" value={newAdmin.email} onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}` }} />
              <input type="password" placeholder="Contraseña inicial" value={newAdmin.password} onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}` }} />
              <select value={newAdmin.adminLevel} onChange={e => setNewAdmin({ ...newAdmin, adminLevel: Number(e.target.value), managerUserId: Number(e.target.value) === 1 && isN2 ? String(data.actor.id) : '' })} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                {!isN2 && <option value={2}>Administrador N2</option>}
                <option value={1}>Administrador N1</option>
              </select>
              {Number(newAdmin.adminLevel) === 1 && <select value={newAdmin.managerUserId} onChange={e => setNewAdmin({ ...newAdmin, managerUserId: e.target.value })} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                <option value="">Selecciona N2 superior…</option>
                {managers.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>}
              <button onClick={createAdmin} disabled={saving} style={{ padding: 10, border: 0, borderRadius: 8, cursor: 'pointer' }}>{saving ? 'Guardando…' : 'Crear administrador'}</button>
            </div>
          </div>}
        </div>

        {selected && <>
          <div style={{ marginTop: 16, border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>📁 Ficha administrativa</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 10 }}>
              <div><small style={{ color: muted }}>Nombre</small><div>{selected.name || '—'}</div></div>
              <div><small style={{ color: muted }}>Correo</small><div>{selected.email || '—'}</div></div>
              <div><small style={{ color: muted }}>Nivel</small><div>Administrador N{selected.adminLevel || 2}</div></div>
              <div><small style={{ color: muted }}>Estado</small><div>{selected.accountStatus || 'ACTIVA'}</div></div>
              <div><small style={{ color: muted }}>Superior</small><div>{selected.managerUserId ? ((data.admins || []).find(a => Number(a.id) === Number(selected.managerUserId))?.name || `#${selected.managerUserId}`) : 'Sin superior'}</div></div>
              <div><small style={{ color: muted }}>Dependientes directos</small><div>{(data.directReports || []).length}</div></div>
            </div>
          </div>

          <div style={{ marginTop: 16, border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>⚙️ Cuenta y jerarquía</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} disabled={!data.canEditSelected || saving} placeholder="Nombre" style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }} />
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!data.canEditSelected || saving} placeholder="Correo" style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }} />
              <select value={form.adminLevel} onChange={e => setForm({ ...form, adminLevel: Number(e.target.value) })} disabled={!data.canEditSelected || saving} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                <option value={1}>Administrador N1</option>
                <option value={2}>Administrador N2</option>
              </select>
              <select value={form.managerUserId || ''} onChange={e => setForm({ ...form, managerUserId: e.target.value })} disabled={!data.canEditSelected || saving || Number(form.adminLevel) !== 1} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}>
                <option value="">Sin superior</option>
                {managers.filter(a => Number(a.id) !== Number(selected.id)).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <select value={form.accountStatus} onChange={e => setForm({ ...form, accountStatus: e.target.value })} disabled={!data.canEditSelected || saving} style={{ padding: 9, borderRadius: 7, border: `1px solid ${border}`, background: card, color: text }}><option>ACTIVA</option><option>INACTIVA</option><option>SUSPENDIDA</option></select>
            </div>
            <button onClick={saveAccount} disabled={!data.canEditSelected || saving} style={{ marginTop: 12, padding: 9, border: 0, borderRadius: 8 }}>{saving ? 'Guardando…' : 'Guardar cuenta y jerarquía'}</button>
          </div>

          {data.canManagePeerManagementDelegation && <div style={{ marginTop: 16, border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>🤝 Delegación entre administradores</h3>
            <p style={{ color: muted, fontSize: 12 }}>Esta delegación permite que el N2 seleccionado administre otros administradores N2 según las reglas existentes.</p>
            <button type="button" onClick={togglePeerManagementDelegation} disabled={saving} style={{ padding: 9, border: 0, borderRadius: 8 }}>
              {saving ? 'Guardando…' : selected.adminPeerManagementDelegated ? 'Revocar delegación N2' : 'Conceder delegación N2'}
            </button>
          </div>}

          <div style={{ marginTop: 16, border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>🎯 Alcance administrativo</h3>
            <p style={{ color: muted, fontSize: 12, marginTop: 0 }}>El alcance forma parte de la ficha del administrador. Un SuperAdministrador Empresarial tiene alcance total de su empresa y no necesita asignaciones manuales.</p>
            {scopeData?.actorHasFullCompanyScope && <div style={{ marginBottom: 12, padding: 11, borderRadius: 8, border: '1px solid #9ae6b4', background: isDarkMode ? '#123524' : '#f0fff4', color: isDarkMode ? '#c6f6d5' : '#276749' }}><strong>🟢 Tu cuenta tiene alcance total de la empresa.</strong><div style={{ fontSize: 12, marginTop: 3 }}>Puedes administrar el alcance de los administradores que tu jerarquía permite. El alcance total no se configura como una lista de recursos.</div></div>}
            {scopeData && !scopeData.canEditSelected && <div style={{ marginBottom: 12, color: muted }}>Solo lectura: tu jerarquía actual no permite modificar el alcance de este administrador.</div>}
            {scopeData && ['EMPLOYEE', 'DEPARTMENT', 'SITE', 'OPERATION'].map(type => (
              <div key={type} style={{ marginTop: 14 }}>
                <h4 style={{ margin: '0 0 7px' }}>{SCOPE_LABELS[type]}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 7 }}>
                  {scopeList(type).map(item => <label key={item.id} style={{ display: 'flex', gap: 7, alignItems: 'center', padding: 8, border: `1px solid ${border}`, borderRadius: 7 }}><input type="checkbox" checked={scopeSelected[type].has(String(item.id))} disabled={!scopeData.canEditSelected || savingScope} onChange={() => toggleScope(type, item.id)} /><span style={{ fontSize: 12 }}>{item.label}</span></label>)}
                  {!scopeList(type).length && <span style={{ color: muted, fontSize: 12 }}>No hay elementos disponibles.</span>}
                </div>
              </div>
            ))}
            {scopeData && <button type="button" disabled={!scopeData.canEditSelected || savingScope} onClick={saveScope} style={{ marginTop: 14, padding: '10px 16px', border: 0, borderRadius: 8, cursor: scopeData.canEditSelected ? 'pointer' : 'not-allowed', opacity: scopeData.canEditSelected ? 1 : 0.55 }}>{savingScope ? 'Guardando…' : 'Guardar alcance'}</button>}
          </div>

          <div style={{ marginTop: 16, border: `1px solid ${border}`, borderRadius: 10, padding: 15 }}>
            <h3 style={{ marginTop: 0 }}>🔐 Permisos operativos</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 8 }}>
              {Object.keys(PERMISSION_LABELS).map(key => <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 9, border: `1px solid ${border}`, borderRadius: 8 }}><input type="checkbox" checked={Boolean(permissions[key])} disabled={!data.canEditSelected || saving} onChange={() => togglePermission(key)} /><span>{PERMISSION_LABELS[key]}</span></label>)}
            </div>
            <button onClick={savePermissions} disabled={!data.canEditSelected || saving} style={{ marginTop: 12, padding: 9, border: 0, borderRadius: 8 }}>{saving ? 'Guardando…' : 'Guardar permisos'}</button>
            <p style={{ color: muted, fontSize: 12, marginBottom: 0 }}>Los permisos determinan operaciones; el alcance determina los recursos. Las reglas jerárquicas del backend siguen siendo la autoridad.</p>
          </div>
        </>}
      </div>
    </section>
  );
}
